import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { X, Check, Loader2, FileText, Search, Plus } from "lucide-react";

const PROPERTY_TYPES = [
  "Hotel",
  "Resort",
  "Motel / Inn",
  "Boutique Hotel",
  "Serviced Apartments",
  "Short-Stay / Vacation Rental",
  "Bed & Breakfast",
  "Other",
];

const TIMEFRAMES = [
  { value: "urgent",   label: "ASAP (within 2 weeks)" },
  { value: "1_month",  label: "Within 1 month" },
  { value: "1_3_months", label: "1 – 3 months" },
  { value: "3_6_months", label: "3 – 6 months" },
  { value: "planning", label: "Planning / no deadline" },
];

/**
 * Category-wide RFQ modal. Broadcasts one buyer RFQ to the top-ranked listings in a category.
 * Required buyer fields (iter25): Property Type, Location, Quantity/Rooms, Timeframe.
 */
// Iter48: Capped RFQ Matcher — server enforces the same bounds, mirrored here for UX.
const RFQ_CAP_MIN = 5;
const RFQ_CAP_MAX = 8;

export default function CategoryRFQModal({ open, onClose, category, topListings, inline = false, source = "site", onStarted, onSubmitted }) {
  const startedRef = React.useRef(false);
  const { user } = useAuth();
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_website: "",
    customer_phone: "",
    hotel: "",
    product_name: "",
    property_type: "",
    delivery_location: "",
    quantity: "",
    target_delivery: "",  // timeframe
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  // Editable recipient list — seeded from topListings but buyer can add/remove
  const [recipients, setRecipients] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const isSubcategory = Boolean(category?.parent_id);
  const [scopedLoading, setScopedLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setShowAdd(false);
    setSearch("");
    setSearchResults([]);
    if (!isSubcategory) {
      // Iter48: Only paid suppliers receive matched RFQs; take top 5 as the default (buyer can add up to RFQ_CAP_MAX = 8 by search).
      const paid = (topListings || []).filter((l) => l.tier && l.tier !== "free");
      setRecipients(paid.slice(0, RFQ_CAP_MIN));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, topListings]);

  // Subcategory pages: recipients are strictly the paid suppliers listed in that subcategory (top 4, never padded).
  useEffect(() => {
    if (!open || !isSubcategory || !category?.id) return;
    let cancelled = false;
    setScopedLoading(true);
    setRecipients([]);
    api.get(`/listings?category_id=${category.id}&limit=40`).then(({ data }) => {
      if (cancelled) return;
      const paid = (data.items || []).filter((l) => l.tier && l.tier !== "free");
      setRecipients(paid.slice(0, 4));
    }).catch(() => { if (!cancelled) setRecipients([]); }).finally(() => { if (!cancelled) setScopedLoading(false); });
    return () => { cancelled = true; };
  }, [open, isSubcategory, category?.id]);

  useEffect(() => {
    if (open && user) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_email: f.customer_email || user.email || "",
        hotel: f.hotel || user.company || "",
      }));
    }
    if (open) setDone(null);
  }, [open, user]);

  if (!open) return null;
  const set = (k) => (e) => {
    if (!startedRef.current) { startedRef.current = true; onStarted?.(); }
    setForm({ ...form, [k]: e.target.value });
  };

  const removeRecipient = (id) => setRecipients((rs) => rs.filter((r) => r.id !== id));

  const doSearch = async (term) => {
    setSearch(term);
    if (!term.trim() || !category) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const param = category.parent_id === null ? "parent_category_id" : "category_id";
      const qp = new URLSearchParams();
      qp.set(param, category.id);
      qp.set("q", term.trim());
      qp.set("limit", "10");
      const { data } = await api.get(`/listings?${qp.toString()}`);
      const existing = new Set(recipients.map((r) => r.id));
      // Iter48: exclude free-tier suppliers from the matched RFQ recipient search
      setSearchResults((data.items || []).filter((it) => !existing.has(it.id) && it.tier && it.tier !== "free"));
    } catch (err) {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const addRecipient = (listing) => {
    if (recipients.some((r) => r.id === listing.id)) return;
    if (recipients.length >= RFQ_CAP_MAX) {
      toast.error(`Max ${RFQ_CAP_MAX} suppliers per RFQ — remove one first to keep replies focused.`);
      return;
    }
    setRecipients((rs) => [...rs, listing]);
    setSearchResults((rs) => rs.filter((r) => r.id !== listing.id));
    toast.success(`Added ${listing.title}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.hotel || !form.property_type || !form.delivery_location || !form.quantity || !form.target_delivery) {
      toast.error("Please complete every required field so suppliers can quote accurately.");
      return;
    }
    if (recipients.length === 0) {
      toast.error("Add at least one supplier to send this RFQ to.");
      return;
    }
    setSubmitting(true);
    try {
      const commonPayload = {
        product_name: form.product_name,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_website: form.customer_website,
        customer_phone: form.customer_phone,
        hotel: form.hotel,
        property_type: form.property_type,
        delivery_location: form.delivery_location,
        quantity: form.quantity,
        target_delivery: form.target_delivery,
        notes: form.notes && form.notes.trim().length > 0
          ? form.notes
          : `RFQ for ${category?.name || "products"}. Property: ${form.hotel} (${form.property_type}). Location: ${form.delivery_location}. Quantity / rooms: ${form.quantity}. Timeframe: ${form.target_delivery}. Please share pricing, MOQ range, and lead-time range.`,
        send_to_similar: false, // exact recipient control — no auto-broadcast
        routing_category_id: isSubcategory ? category.id : "",
        source,
        batch_id: (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`),
      };
      // Fire one RFQ per selected recipient in parallel
      const results = await Promise.allSettled(
        recipients.map((r) => api.post("/rfq", { ...commonPayload, listing_id: r.id })),
      );
      const sent = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - sent;
      if (sent === 0) {
        toast.error("Failed to send RFQ to any supplier. Please try again.");
        return;
      }
      if (failed > 0) toast.warning(`${sent} sent · ${failed} failed`);
      setDone({ total: sent });
      onSubmitted?.(sent);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong sending your RFQ. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const Wrapper = inline
    ? ({ children }) => <div className="bg-white border border-[#E8ECE9] shadow-xl shadow-[#11211A]/5 w-full" data-testid="category-rfq-inline">{children}</div>
    : ({ children }) => (
      <div className="fixed inset-0 z-50 bg-[#11211A]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="category-rfq-modal" onClick={onClose}>
        <div className="bg-white max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>{children}</div>
      </div>
    );
  return (
    <Wrapper>
        <div className="px-6 py-4 border-b border-[#E8ECE9] flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="tracking-overline mb-1 flex items-center gap-2"><FileText className="w-3 h-3" /> {inline ? "Quick quote request" : "Category RFQ"}</div>
            <div className="font-serif text-xl text-[#11211A]">Request Quotes in {category?.name}</div>
          </div>
          {!inline && (
            <button onClick={onClose} className="text-[#7D8C85] hover:text-[#11211A]" data-testid="category-rfq-close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="category-rfq-done">
            <div className="w-12 h-12 rounded-full bg-[#264839] text-white flex items-center justify-center mx-auto">
              <Check className="w-5 h-5" />
            </div>
            <div className="font-serif text-xl text-[#11211A] mt-4">Sent to {done.total} supplier{done.total === 1 ? "" : "s"}</div>
            <p className="text-sm text-[#7D8C85] mt-2">
              You'll receive quotes directly in your inbox at <strong>{form.customer_email}</strong>. Suppliers typically reply within 24 hours.
            </p>
            <button onClick={() => (onClose ? onClose() : window.location.reload())} className="btn-primary mt-6" data-testid="category-rfq-done-close">{inline ? "Send another request" : "Done"}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <p className="text-xs text-[#7D8C85]">
              {isSubcategory
                ? <>One form → quotes from up to 4 suppliers listed in <strong>{category.name}</strong> only — we never pad with unrelated suppliers. Fields marked * are required so suppliers can price accurately.</>
                : "One form → quotes from up to 4 top-ranked suppliers in this category. Fields marked * are required so suppliers can price accurately."}
            </p>
            <div className="p-3 bg-[#F0EFEA] border-l-4 border-[#B35C4A]">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#B35C4A] mb-1">What are you looking for? *</label>
              <input
                required
                value={form.product_name}
                onChange={set("product_name")}
                placeholder="e.g. 5-Star Hotel Towels"
                className="w-full px-3 py-2 text-base font-serif border border-[#C4CFC9] rounded-sm bg-white"
                data-testid="crfq-product-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Your name *</label>
                <input required value={form.customer_name} onChange={set("customer_name")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="crfq-name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Work email *</label>
                <input required type="email" value={form.customer_email} onChange={set("customer_email")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="crfq-email" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Phone (optional)</label>
                <input value={form.customer_phone} onChange={set("customer_phone")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="crfq-phone" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Web address (optional)</label>
                <input value={form.customer_website} onChange={set("customer_website")} placeholder="e.g. grand-palace-hotel.com" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="crfq-website" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Property / hotel name *</label>
                <input required value={form.hotel} onChange={set("hotel")} placeholder="e.g. The Grand Palace" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="crfq-hotel" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Property type *</label>
                <select required value={form.property_type} onChange={set("property_type")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm bg-white" data-testid="crfq-property-type">
                  <option value="">— Select —</option>
                  {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Location *</label>
                <input required value={form.delivery_location} onChange={set("delivery_location")} placeholder="City, Country" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="crfq-location" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Quantity / rooms *</label>
                <input required value={form.quantity} onChange={set("quantity")} placeholder="e.g. 120 rooms · 500 sheets" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="crfq-quantity" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Timeframe *</label>
                <select required value={form.target_delivery} onChange={set("target_delivery")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm bg-white" data-testid="crfq-timeframe">
                  <option value="">— Select —</option>
                  {TIMEFRAMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-[#4A5550] mb-1">Anything else? <span className="text-[#7D8C85] font-normal">(optional — colour, spec, MOQ preference)</span></label>
                <textarea value={form.notes} onChange={set("notes")} rows={3} placeholder="Any relevant detail on quality, branding, MOQ etc." className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="crfq-notes" />
              </div>
            </div>
            {/* Editable recipient list — buyer can add/remove suppliers before sending */}
            <div className="border border-[#E8ECE9] bg-[#F9F8F5] p-3 text-xs text-[#4A5550] mt-3" data-testid="crfq-recipients">
              <div className="flex items-center justify-between mb-2">
                <div className="tracking-overline">
                  Sending to <span data-testid="crfq-recipient-count">{recipients.length}</span> supplier{recipients.length === 1 ? "" : "s"}
                  <span className="text-[#7D8C85] normal-case font-normal tracking-normal ml-2">(max {RFQ_CAP_MAX} · paid suppliers only)</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAdd((v) => !v)}
                  className="text-[11px] text-[#264839] hover:underline inline-flex items-center gap-1 font-medium"
                  data-testid="crfq-add-more-btn"
                >
                  <Plus className="w-3 h-3" /> {showAdd ? "Close" : "Add more"}
                </button>
              </div>
              {scopedLoading ? (
                <div className="text-[#7D8C85] italic" data-testid="crfq-recipients-loading">Finding suppliers listed in {category.name}…</div>
              ) : recipients.length === 0 ? (
                <div className="text-[#B35C4A] italic" data-testid="crfq-recipients-empty">
                  {isSubcategory ? `No paid suppliers are listed in ${category.name} yet — add one below or try the parent category.` : "No suppliers selected — add at least one below."}
                </div>
              ) : (
                <>
                {isSubcategory && recipients.length < 4 && (
                  <div className="text-[#7D8C85] mb-2" data-testid="crfq-scoped-note">Only {recipients.length} paid supplier{recipients.length === 1 ? " is" : "s are"} listed in {category.name} — sending to {recipients.length === 1 ? "that one" : "those"} only.</div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {recipients.map((l) => (
                    <span
                      key={l.id}
                      className="inline-flex items-center gap-1 bg-white border border-[#C4CFC9] px-2 py-0.5"
                      data-testid={`crfq-recipient-${l.id}`}
                    >
                      {l.title}
                      <button
                        type="button"
                        onClick={() => removeRecipient(l.id)}
                        className="ml-1 text-[#7D8C85] hover:text-red-600 leading-none"
                        aria-label={`Remove ${l.title}`}
                        data-testid={`crfq-recipient-remove-${l.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                </>
              )}
              {showAdd && (
                <div className="mt-3 pt-3 border-t border-[#E8ECE9]" data-testid="crfq-add-panel">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#7D8C85]" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => doSearch(e.target.value)}
                      placeholder={`Search suppliers in ${category?.name || "this category"}…`}
                      className="w-full pl-7 pr-3 py-1.5 text-xs border border-[#C4CFC9] rounded-sm bg-white"
                      data-testid="crfq-add-search"
                    />
                  </div>
                  {searching && <div className="text-[10px] text-[#7D8C85] mt-1.5">Searching…</div>}
                  {!searching && search.trim() && searchResults.length === 0 && (
                    <div className="text-[10px] text-[#7D8C85] italic mt-1.5">No more suppliers match — try a different keyword.</div>
                  )}
                  {searchResults.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto border border-[#E8ECE9] bg-white rounded-sm">
                      {searchResults.map((s) => (
                        <button
                          type="button"
                          key={s.id}
                          onClick={() => addRecipient(s)}
                          className="w-full text-left px-2 py-1.5 text-xs border-b border-[#E8ECE9] last:border-b-0 hover:bg-[#F0EFEA] flex items-center justify-between gap-2"
                          data-testid={`crfq-add-result-${s.id}`}
                        >
                          <span className="truncate">{s.title}</span>
                          <Plus className="w-3 h-3 text-[#264839] shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-2 mt-2">
              {!inline && <button type="button" onClick={onClose} className="btn-ghost" data-testid="crfq-cancel">Cancel</button>}
              <button type="submit" disabled={submitting || recipients.length === 0} className="btn-primary disabled:opacity-50" data-testid="crfq-submit">
                {submitting
                  ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>)
                  : `Send RFQ to ${recipients.length} supplier${recipients.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </form>
        )}
    </Wrapper>
  );
}
